import { useEffect, useRef } from "react";
import { MapPin, Phone } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { loadGoogleMaps } from "@/lib/google-maps-loader";

export interface DesmonteSugerido {
  nome: string;
  telefone: string | null;
  endereco: string;
  latitude: string | number;
  longitude: string | number;
  distanciaKm: string | number;
}

export interface SinistroLocalizacaoData {
  endereco: string;
  latitude: string | number;
  longitude: string | number;
  desmontes: DesmonteSugerido[];
}

export function SinistroLocalizacaoViewDialog({
  open,
  onOpenChange,
  localizacao,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  localizacao: SinistroLocalizacaoData;
}) {
  const mapDivRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    loadGoogleMaps().then((g) => {
      if (cancelled || !mapDivRef.current) return;

      const center = {
        lat: Number(localizacao.latitude),
        lng: Number(localizacao.longitude),
      };
      const map = new g.maps.Map(mapDivRef.current, {
        center,
        zoom: 11,
        mapId: "SINISTRO_LOCALIZACAO_VIEW",
      });

      new g.maps.marker.AdvancedMarkerElement({
        map,
        position: center,
        title: "Local do sinistro",
      });

      for (const desmonte of localizacao.desmontes) {
        new g.maps.marker.AdvancedMarkerElement({
          map,
          position: {
            lat: Number(desmonte.latitude),
            lng: Number(desmonte.longitude),
          },
          title: desmonte.nome,
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [open, localizacao]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Localização do Sinistro
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-slate-600">{localizacao.endereco}</p>
        <div ref={mapDivRef} className="h-64 w-full rounded-md border" />

        <div className="flex-1 overflow-y-auto">
          <h3 className="mb-2 mt-2 text-sm font-semibold text-slate-700">
            Desmontes sugeridos em um raio de 100km
          </h3>
          {localizacao.desmontes.length === 0 ? (
            <p className="text-sm text-slate-500">
              Nenhum desmonte encontrado no raio de 100km.
            </p>
          ) : (
            <ul className="space-y-2">
              {localizacao.desmontes.map((desmonte) => (
                <li
                  key={`${desmonte.nome}-${desmonte.endereco}`}
                  className="rounded-md border p-2 text-sm"
                >
                  <p className="font-medium text-slate-900">{desmonte.nome}</p>
                  <p className="text-slate-600">{desmonte.endereco}</p>
                  <div className="mt-1 flex items-center gap-3 text-xs text-slate-500">
                    {desmonte.telefone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {desmonte.telefone}
                      </span>
                    )}
                    <span>{Number(desmonte.distanciaKm).toFixed(1)} km</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
